import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { ImageUploadField } from '@/components/ui/image-upload-field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useBusinesses } from '@/features/business/use-businesses';
import { fromInputValue, toInputValue } from '@/lib/store-time';
import { useDeleteInfoCard, useSaveInfoCard } from './info-cards.api';
import { AGE_GROUP, TARGET_GENDER, type InfoCard } from './types';

/**
 * The info card editor.
 *
 * The original was one accordion of every field in one column, with English
 * and Spanish interleaved — so writing the Spanish copy meant scrolling past
 * the English body, the English HTML body and the English action text. Here the
 * bilingual half is two tabs of identical shape and everything else is grouped
 * by what it decides: where the card shows, when, and what it does when tapped.
 */

const ALL_STORES = 'ALL';

/**
 * The half of the form that exists once per language.
 *
 * Declared at module scope, not inside the editor. A component defined in a
 * render body is a new type on every render, so React unmounts and remounts the
 * whole subtree — which for a panel of controlled inputs means losing focus
 * after every keystroke.
 */
function CopyFields({
  language,
  draft,
  busy,
  set,
  setBody,
}: {
  language: 'Eng' | 'Spa';
  draft: InfoCard;
  busy: boolean;
  set: <K extends keyof InfoCard>(key: K, value: InfoCard[K]) => void;
  setBody: (language: 'Eng' | 'Spa', value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 pt-4">
      <Field label={t('infoCards.cardTitle')} htmlFor={`title${language}`}>
        <Input
          id={`title${language}`}
          value={draft[`title${language}`] ?? ''}
          disabled={busy}
          onChange={(event) => set(`title${language}`, event.target.value)}
        />
      </Field>
      <Field label={t('infoCards.body')} htmlFor={`body${language}`}>
        <Textarea
          id={`body${language}`}
          rows={3}
          value={draft[`body${language}`] ?? ''}
          disabled={busy}
          onChange={(event) => setBody(language, event.target.value)}
        />
      </Field>
      <Field
        label={t('infoCards.htmlBody')}
        htmlFor={`html${language}`}
        hint={t('infoCards.htmlBodyHint')}
      >
        <Textarea
          id={`html${language}`}
          rows={2}
          value={draft[`htmlBody${language}`] ?? ''}
          disabled={busy}
          onChange={(event) => set(`htmlBody${language}`, event.target.value)}
        />
      </Field>
      <Field label={t('infoCards.actionText')} htmlFor={`action${language}`}>
        <Input
          id={`action${language}`}
          value={draft[`action${language}`] ?? ''}
          disabled={busy}
          onChange={(event) => set(`action${language}`, event.target.value)}
        />
      </Field>
      <ImageUploadField
        container="infocard"
        label={t('infoCards.image')}
        hint={t('infoCards.imageHint')}
        disabled={busy}
        value={(language === 'Eng' ? draft.imageUrl : draft.imageUrl2) ?? ''}
        onChange={(url) =>
          set(language === 'Eng' ? 'imageUrl' : 'imageUrl2', url)
        }
      />
    </div>
  );
}

export function InfoCardEditor({
  card,
  open,
  onOpenChange,
}: {
  card: InfoCard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<InfoCard | null>(card);

  const { data: businesses } = useBusinesses();
  const save = useSaveInfoCard();
  const remove = useDeleteInfoCard();

  useEffect(() => {
    setDraft(card);
  }, [card]);

  if (!draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl" />
      </Sheet>
    );
  }

  const isEdit = !!draft.id;
  const busy = save.isPending || remove.isPending;

  const set = <K extends keyof InfoCard>(key: K, value: InfoCard[K]) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  /** The body is stored twice; both copies come from one box. */
  const setBody = (language: 'Eng' | 'Spa', value: string) =>
    setDraft((current) =>
      current
        ? { ...current, [`body${language}`]: value, [`body${language}2`]: value }
        : current
    );

  const startsAfterEnds =
    !!draft.startDateTime &&
    !!draft.endDateTime &&
    Date.parse(`${draft.startDateTime}Z`) >= Date.parse(`${draft.endDateTime}Z`);

  const canSave =
    !!draft.name?.trim() &&
    !!draft.startDateTime &&
    !!draft.endDateTime &&
    !startsAfterEnds;

  function submit() {
    save.mutate(draft!, {
      onSuccess: () => {
        toast.success(isEdit ? t('infoCards.updated') : t('infoCards.created'));
        onOpenChange(false);
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('infoCards.saveFailed')
        ),
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit ? t('infoCards.editCard') : t('infoCards.newCard')}
          </SheetTitle>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={busy}
                >
                  <Trash2 className="size-3.5" />
                  {t('infoCards.archive')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('infoCards.archive')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('infoCards.archiveConfirm', { name: draft.name ?? '' })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(draft.id!, {
                        onSuccess: () => {
                          toast.success(t('infoCards.archived'));
                          onOpenChange(false);
                        },
                        onError: () => toast.error(t('infoCards.saveFailed')),
                      })
                    }
                  >
                    {t('infoCards.archive')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-6 p-4">
          <Field
            label={t('infoCards.name')}
            htmlFor="name"
            hint={t('infoCards.nameHint')}
          >
            <Input
              id="name"
              value={draft.name ?? ''}
              disabled={busy}
              onChange={(event) => set('name', event.target.value)}
            />
          </Field>

          {/* --------------------------------------------------- content */}
          <Section title={t('infoCards.content')}>
            <Tabs defaultValue="Eng">
              <TabsList>
                <TabsTrigger value="Eng">{t('common.english')}</TabsTrigger>
                <TabsTrigger value="Spa">{t('common.spanish')}</TabsTrigger>
              </TabsList>
              <TabsContent value="Eng">
                <CopyFields
                  language="Eng"
                  draft={draft}
                  busy={busy}
                  set={set}
                  setBody={setBody}
                />
              </TabsContent>
              <TabsContent value="Spa">
                <CopyFields
                  language="Spa"
                  draft={draft}
                  busy={busy}
                  set={set}
                  setBody={setBody}
                />
              </TabsContent>
            </Tabs>

            <Field
              label={t('infoCards.actionUrl')}
              htmlFor="actionUrl"
              hint={t('infoCards.actionUrlHint')}
            >
              <Input
                id="actionUrl"
                value={draft.actionUrl ?? ''}
                disabled={busy}
                placeholder="https://…"
                onChange={(event) => set('actionUrl', event.target.value)}
              />
            </Field>
          </Section>

          {/* -------------------------------------------------- schedule */}
          <Section title={t('infoCards.schedule')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('infoCards.starts')} htmlFor="start">
                <Input
                  id="start"
                  type="datetime-local"
                  value={toInputValue(draft.startDateTime)}
                  disabled={busy}
                  onChange={(event) =>
                    set('startDateTime', fromInputValue(event.target.value))
                  }
                />
              </Field>
              <Field label={t('infoCards.ends')} htmlFor="end">
                <Input
                  id="end"
                  type="datetime-local"
                  value={toInputValue(draft.endDateTime)}
                  disabled={busy}
                  onChange={(event) =>
                    set('endDateTime', fromInputValue(event.target.value))
                  }
                />
              </Field>
            </div>
            {startsAfterEnds && (
              <p className="text-destructive text-xs">
                {t('infoCards.endBeforeStart')}
              </p>
            )}
          </Section>

          {/* ------------------------------------------------- placement */}
          <Section title={t('infoCards.placement')}>
            <Toggle
              label={t('infoCards.isActive')}
              hint={t('infoCards.isActiveHint')}
              checked={draft.isActive}
              disabled={busy}
              onChange={(value) => set('isActive', value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle
                label={t('infoCards.showInApp')}
                checked={draft.showInApp}
                disabled={busy}
                onChange={(value) => set('showInApp', value)}
              />
              <Toggle
                label={t('infoCards.showInWeb')}
                checked={draft.showInWeb}
                disabled={busy}
                onChange={(value) => set('showInWeb', value)}
              />
            </div>

            <Field
              label={t('infoCards.store')}
              hint={t('infoCards.storeHint')}
            >
              <Select
                value={draft.businessId ?? ALL_STORES}
                disabled={busy}
                onValueChange={(value) =>
                  set('businessId', value === ALL_STORES ? null : value)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STORES}>
                    {t('infoCards.allStores')}
                  </SelectItem>
                  {(businesses ?? []).map((business) => (
                    <SelectItem key={business.id} value={business.id}>
                      {business.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('infoCards.ordering')}
                htmlFor="ordering"
                hint={t('infoCards.orderingHint')}
              >
                <Input
                  id="ordering"
                  type="number"
                  value={draft.ordering}
                  disabled={busy}
                  onChange={(event) =>
                    set('ordering', Number(event.target.value))
                  }
                />
              </Field>
              <div className="space-y-3">
                <Toggle
                  label={t('infoCards.defaultHeight')}
                  checked={draft.useDefaultHeight}
                  disabled={busy}
                  onChange={(value) => set('useDefaultHeight', value)}
                />
                {!draft.useDefaultHeight && (
                  <Field label={t('infoCards.height')} htmlFor="height">
                    <Input
                      id="height"
                      type="number"
                      value={draft.height}
                      disabled={busy}
                      onChange={(event) =>
                        set('height', Number(event.target.value))
                      }
                    />
                  </Field>
                )}
              </div>
            </div>
          </Section>

          {/* ------------------------------------------------- targeting */}
          <Section title={t('infoCards.targeting')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('infoCards.gender')}>
                <Select
                  value={String(draft.gender)}
                  disabled={busy}
                  onValueChange={(value) => set('gender', Number(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(TARGET_GENDER.any)}>
                      {t('infoCards.genderAny')}
                    </SelectItem>
                    <SelectItem value={String(TARGET_GENDER.male)}>
                      {t('infoCards.genderMale')}
                    </SelectItem>
                    <SelectItem value={String(TARGET_GENDER.female)}>
                      {t('infoCards.genderFemale')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('infoCards.ageRange')}>
                <Select
                  value={String(draft.ageRange)}
                  disabled={busy}
                  onValueChange={(value) => set('ageRange', Number(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(AGE_GROUP.all)}>
                      {t('infoCards.ageAll')}
                    </SelectItem>
                    <SelectItem value={String(AGE_GROUP.minor)}>
                      {t('infoCards.ageMinor')}
                    </SelectItem>
                    <SelectItem value={String(AGE_GROUP.teenager)}>
                      {t('infoCards.ageTeenager')}
                    </SelectItem>
                    <SelectItem value={String(AGE_GROUP.middleAged)}>
                      {t('infoCards.ageMiddleAged')}
                    </SelectItem>
                    <SelectItem value={String(AGE_GROUP.senior)}>
                      {t('infoCards.ageSenior')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || busy} onClick={submit}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('infoCards.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
